// Tek bir eklenti raporu (toolkit T4): temsilcinin "Sorun bildir"i ya da admin'in `snapshot` komutu. Not, ekran
// görüntüleri (2.5.0, T4b), ortam, durum raporunun önemli alanları + tamamı, son günlük satırları; durum Yeni →
// İnceleniyor → Çözüldü.
// Eklenti sayfasının rapor kutusu ve Eklenti Sağlığı çekmecesi (zaman çizelgesi / Hatalar) açar.

import { useEffect, useState } from 'react';
import { extReportImageUrl, getExtReport, setExtReportStatus } from '../api';
import { brandLabel, tzOfBrands } from '../constants';
import { L, fmtRejectedRow, rejectedRowOf, type Lang } from '../ext-health';
import { fmtClock } from '../format';
import type { BrandId, ExtReport, ExtReportStatus } from '../types';
import { Icon, Modal } from '../ui';

const MONO = 'ui-monospace,SFMono-Regular,Menlo,monospace';
export const REPORT_STATUS_META: Record<ExtReportStatus, { tr: string; en: string; color: string }> = {
  new: { tr: 'Yeni', en: 'New', color: '#ef5d5d' },
  looking: { tr: 'İnceleniyor', en: 'Looking', color: '#e0a23c' },
  fixed: { tr: 'Çözüldü', en: 'Fixed', color: '#37c46b' },
};
export const installLabel = (t: string | undefined, lang: Lang): string => {
  if (!t) return '';
  if (t === 'normal') return L(lang, 'Mağaza kurulumu', 'Store install');
  if (t === 'development') return L(lang, 'Manuel kurulum', 'Manual build');
  if (t === 'sideload') return L(lang, 'Yan yükleme', 'Sideloaded');
  if (t === 'admin') return L(lang, 'Yönetici kurulumu', 'Admin-installed');
  return t;
};

/** Durum raporunun ilk bakışta okunacak alanları (tamamı aşağıda açılır). */
const DIAG_KEYS: [string, string, string][] = [
  ['version', 'Sürüm', 'Version'], ['fsmState', 'Durum makinesi', 'State machine'], ['sheet', 'Aktif sayfa', 'Active sheet'],
  ['sessionState', 'Oturum', 'Session'], ['walTotal', 'Bekleyen sonuç', 'Pending results'], ['rejectedRow', 'Atlanan satır', 'Skipped row'],
  ['lastPollAgeMs', 'Son yoklama (ms)', 'Last poll (ms)'], ['stale', 'Bağlam kopuk', 'Context stale'],
];

export default function ExtReportModal({ id, lang, onClose, onChanged }: {
  id: string; lang: Lang; onClose: () => void; onChanged?: () => void;
}) {
  const [rep, setRep] = useState<ExtReport | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let alive = true;
    getExtReport(id).then((r) => { if (alive) setRep(r); }).catch((e) => { if (alive) setErr(e instanceof Error ? e.message : 'load_failed'); });
    return () => { alive = false; };
  }, [id]);

  const setStatus = async (s: ExtReportStatus) => {
    if (!rep || rep.status === s) return;
    setBusy(true);
    try {
      await setExtReportStatus(rep.id, s);
      setRep({ ...rep, status: s });
      onChanged?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'save_failed');
    } finally {
      setBusy(false);
    }
  };

  const tz = rep ? tzOfBrands([rep.brandKey as BrandId]) : 'Europe/Istanbul';
  const d = rep?.diagnostics || {};
  const phone = d.phone && typeof d.phone === 'object' ? (d.phone as Record<string, unknown>) : null;

  return (
    <Modal onClose={onClose} maxWidth={720}>
      {!rep ? (
        <div style={{ fontSize: '12.5px', color: err ? 'var(--bad-text)' : 'var(--faint)' }}>{err ? L(lang, 'Okunamadı: ' + err, 'Could not load: ' + err) : 'Okunuyor…'}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: '78vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <Icon name={rep.source === 'agent' ? 'flag' : 'photo_camera'} size={20} color="var(--accent)" />
            <span style={{ fontSize: '16px', fontWeight: 700 }}>{rep.agentName}</span>
            <span style={{ fontSize: '9.5px', fontWeight: 700, color: 'var(--dim)', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '5px', padding: '2px 6px' }}>{brandLabel(rep.brandKey)}</span>
            <span style={{ fontSize: '12px', color: 'var(--dim)' }}>
              {(rep.source === 'agent' ? L(lang, 'Temsilci bildirdi', 'Reported by the agent') : L(lang, 'Anlık görüntü (admin)', 'Snapshot (admin)'))
                + ' · ' + new Date(rep.createdAt).toLocaleDateString(lang === 'en' ? 'en-GB' : 'tr-TR', { day: '2-digit', month: '2-digit', timeZone: tz }) + ' ' + fmtClock(rep.createdAt, tz)}
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
              {(['new', 'looking', 'fixed'] as ExtReportStatus[]).map((s) => {
                const m = REPORT_STATUS_META[s];
                const on = rep.status === s;
                return (
                  <button
                    key={s}
                    disabled={busy}
                    onClick={() => void setStatus(s)}
                    style={{ fontSize: '11px', fontWeight: 600, padding: '4px 10px', borderRadius: '999px', cursor: busy ? 'wait' : 'pointer', color: on ? m.color : 'var(--dim)', background: on ? m.color + '1a' : 'transparent', border: '1px solid ' + (on ? m.color + '55' : 'var(--border)') }}
                  >
                    {L(lang, m.tr, m.en)}
                  </button>
                );
              })}
            </div>
          </div>

          {rep.note && (
            <div style={{ padding: '11px 13px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '13px', color: 'var(--text)' }}>
              {'“' + rep.note + '”'}
            </div>
          )}

          {rep.images && rep.images.length > 0 && (
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)', marginBottom: '6px' }}>
                {L(lang, 'Ekran görüntüleri (' + rep.images.length + ')', 'Screenshots (' + rep.images.length + ')')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                {rep.images.map((im) => (
                  <a key={im.idx} href={extReportImageUrl(rep.id, im.idx)} target="_blank" rel="noopener noreferrer"
                    title={L(lang, 'Tam boyut aç', 'Open full size')}
                    style={{ display: 'block', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', background: 'var(--inset2)' }}>
                    <img src={extReportImageUrl(rep.id, im.idx)} alt={L(lang, 'Ekran görüntüsü ' + (im.idx + 1), 'Screenshot ' + (im.idx + 1))}
                      loading="lazy" style={{ display: 'block', width: '100%', height: '140px', objectFit: 'contain' }} />
                  </a>
                ))}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--faint)', marginTop: '6px' }}>
                {L(lang, 'Müşteri bilgisi içerebilir — 30 gün sonra silinir.', 'May show customer data — deleted after 30 days.')}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', fontSize: '11.5px', color: 'var(--dim)' }}>
            {[rep.version ? 'v' + rep.version : '', installLabel(rep.env.installType, lang), rep.env.browser || '', rep.env.os || '']
              .filter(Boolean).map((x, i) => <span key={i} style={{ padding: '2px 8px', borderRadius: '6px', border: '1px solid var(--border)' }}>{x}</span>)}
          </div>

          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)', marginBottom: '6px' }}>Durum raporu</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '6px' }}>
              {DIAG_KEYS.filter(([k]) => d[k] !== undefined && d[k] !== null && d[k] !== '').map(([k, tr, en]) => (
                <div key={k} style={{ padding: '7px 10px', background: 'var(--inset)', border: '1px solid var(--border)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '10.5px', color: 'var(--faint)' }}>{L(lang, tr, en)}</div>
                  <div style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 500, fontFamily: MONO }}>{k === 'rejectedRow' ? (rejectedRowOf(d[k]) ? fmtRejectedRow(rejectedRowOf(d[k])!) : '') : String(d[k])}</div>
                </div>
              ))}
              {phone && typeof phone.status === 'string' && (
                <div style={{ padding: '7px 10px', background: 'var(--inset)', border: '1px solid var(--border)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '10.5px', color: 'var(--faint)' }}>Softphone</div>
                  <div style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 500, fontFamily: MONO }}>{phone.status}</div>
                </div>
              )}
            </div>
            <button onClick={() => setShowAll(!showAll)} style={{ marginTop: '8px', background: 'transparent', border: 'none', color: 'var(--accent)', fontSize: '11.5px', cursor: 'pointer', padding: 0 }}>
              {showAll ? L(lang, 'Tüm alanları gizle', 'Hide all fields') : L(lang, 'Tüm alanları göster', 'Show all fields')}
            </button>
            {showAll && (
              <pre style={{ margin: '6px 0 0', padding: '10px', background: 'var(--inset2)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '11px', color: 'var(--dim)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '240px', overflow: 'auto' }}>
                {JSON.stringify(d, null, 1)}
              </pre>
            )}
          </div>

          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)', marginBottom: '6px' }}>
              {L(lang, 'Son günlük satırları (' + rep.lines.length + ')', 'Recent log lines (' + rep.lines.length + ')')}
            </div>
            {rep.lines.length === 0 ? (
              <div style={{ fontSize: '12px', color: 'var(--faint)' }}>{L(lang, 'Satır yok.', 'No lines.')}</div>
            ) : (
              <div style={{ background: 'var(--inset2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '8px 12px', fontFamily: MONO, fontSize: '11px', color: 'var(--code)', maxHeight: '260px', overflow: 'auto' }}>
                {rep.lines.slice().reverse().map((l, i) => <div key={i} style={{ padding: '2px 0', wordBreak: 'break-word' }}>{l}</div>)}
              </div>
            )}
            <div style={{ fontSize: '11px', color: 'var(--faint)', marginTop: '6px' }}>Günlük saatleri UTC'dir. Rakam dizileri ##### olarak maskelenir.</div>
          </div>
        </div>
      )}
    </Modal>
  );
}
